"use client";

import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { UStyles } from "@/app/utils/UStyles";
import { getViewportShift } from "./Tooltip.logic";
import styles from "./Tooltip.module.css";

export enum TooltipPosition {
  TOP = "Top",
  BOTTOM = "Bottom",
  LEFT = "Left",
  RIGHT = "Right",
  TOP_LEFT = "TopLeft",
  TOP_RIGHT = "TopRight",
  BOTTOM_LEFT = "BottomLeft",
  BOTTOM_RIGHT = "BottomRight",
}

export enum TooltipType {
  DARK = "dark",
  GREEN = "green",
  GREEN_DARK = "greenDark",
  NONE = "none",
}

export type Props = {
  trigger: React.ReactNode;
  content: React.ReactNode;
  position: TooltipPosition;
  gap?: number;
  type?: TooltipType;
  className?: string;
  disabled?: boolean;
  portal?: boolean;
};

export function Tooltip(props: Props) {
  const { position, gap, type = TooltipType.DARK } = props;

  const triggerRef = useRef<HTMLSpanElement>(null);
  const portalRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const [shift, setShift] = useState(0);

  const showPortal = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setShift(0);
      setCoords(getPortalAnchor(rect));
    }
  };

  const hidePortal = () => setCoords(null);

  useLayoutEffect(() => {
    if (!coords || !portalRef.current) {
      return;
    }

    const correction = getViewportShift(portalRef.current.getBoundingClientRect());
    if (correction !== 0) {
      setShift((current) => current + correction);
    }
  }, [coords]);

  const getPortalAnchor = (rect: DOMRect): { top: number; left: number } => {
    switch (position) {
      case TooltipPosition.LEFT:
        return { top: rect.top + rect.height / 2, left: rect.left };
      case TooltipPosition.RIGHT:
        return { top: rect.top + rect.height / 2, left: rect.right };
      case TooltipPosition.BOTTOM:
      case TooltipPosition.BOTTOM_LEFT:
      case TooltipPosition.BOTTOM_RIGHT:
        return { top: rect.bottom, left: rect.left + rect.width / 2 };
      default:
        return { top: rect.top, left: rect.left + rect.width / 2 };
    }
  };

  const findPortalPosition = (): string => {
    switch (position) {
      case TooltipPosition.LEFT:
        return styles.portalTextLeft;
      case TooltipPosition.RIGHT:
        return styles.portalTextRight;
      case TooltipPosition.BOTTOM:
      case TooltipPosition.BOTTOM_LEFT:
      case TooltipPosition.BOTTOM_RIGHT:
        return styles.portalTextBottom;
      default:
        return styles.portalTextTop;
    }
  };

  const findPosition = (): string => {
    let css = "";

    switch (position) {
      case TooltipPosition.TOP:
        css = styles.tooltipTextTop;
        break;
      case TooltipPosition.LEFT:
        css = styles.tooltipTextLeft;
        break;
      case TooltipPosition.BOTTOM:
        css = styles.tooltipTextBottom;
        break;
      case TooltipPosition.RIGHT:
        css = styles.tooltipTextRight;
        break;
      case TooltipPosition.TOP_LEFT:
        css = styles.tooltipTextTopLeft;
        break;
      case TooltipPosition.TOP_RIGHT:
        css = styles.tooltipTextTopRight;
        break;
      case TooltipPosition.BOTTOM_LEFT:
        css = styles.tooltipTextBottomLeft;
        break;
      case TooltipPosition.BOTTOM_RIGHT:
        css = styles.tooltipTextBottomRight;
        break;
    }

    return css;
  };

  const findType = (): string => {
    switch (type) {
      case TooltipType.GREEN:
        return styles.green;
      case TooltipType.GREEN_DARK:
        return styles.greenDark;
      case TooltipType.NONE:
        return "";
      default:
        return styles.dark;
    }
  };

  const getGapStyle = (): React.CSSProperties => {
    const inlineStyles: React.CSSProperties = {};

    if (gap !== undefined) {
      switch (position) {
        case TooltipPosition.TOP:
        case TooltipPosition.TOP_LEFT:
        case TooltipPosition.TOP_RIGHT:
          inlineStyles.marginBottom = `${gap}px`;
          break;
        case TooltipPosition.BOTTOM:
        case TooltipPosition.BOTTOM_LEFT:
        case TooltipPosition.BOTTOM_RIGHT:
          inlineStyles.marginTop = `${gap}px`;
          break;
        case TooltipPosition.LEFT:
          inlineStyles.marginRight = `${gap}px`;
          break;
        case TooltipPosition.RIGHT:
          inlineStyles.marginLeft = `${gap}px`;
          break;
      }
    }

    return inlineStyles;
  };

  if (props.portal) {
    return (
      <span
        ref={triggerRef}
        className={styles.portalTrigger}
        onMouseEnter={showPortal}
        onMouseLeave={hidePortal}
      >
        {props.trigger}
        {coords &&
          !props.disabled &&
          typeof document !== "undefined" &&
          createPortal(
            <span
              ref={portalRef}
              className={UStyles.classNames(
                styles.portalText,
                findPortalPosition(),
                findType(),
                props.className,
              )}
              style={
                {
                  top: coords.top,
                  left: coords.left + shift,
                  "--tooltip-shift": `${shift}px`,
                } as React.CSSProperties
              }
            >
              {props.content}
            </span>,
            document.body,
          )}
      </span>
    );
  }

  return (
    <div className={UStyles.classNames(styles.tooltip, findType(), props.className)}>
      <span className={styles.tooltipTrigger}>{props.trigger}</span>
      {!props.disabled && (
        <span
          className={UStyles.classNames(styles.tooltipText, findPosition())}
          style={getGapStyle()}
        >
          {props.content}
        </span>
      )}
    </div>
  );
}
